import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { addYears, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { toCurrency, toCurrencyWithoutLargeCents, toPercent } from "@helpers";
import { PHONE_CHART_HEIGHT, SecuritiesChart } from "@components";
import { usePerformanceTrendColor } from "@hooks";
import { GainLossTypography, GainLossPercents } from "@rentablo/components";
import {
  InvestmentsChartDataStatistic,
  useInvestmentsChartData,
} from "@api/v1/hooks/performance/useInvestmentsChartData";
import { MandatorTypography } from "@rentablo/components";
import { SecuritiesChartSelectedType } from "@components/Chart/SecuritiesChart/types";
import {
  ChartControlsDesktopBar,
  ChartControlsMobileBar,
  ChartControlsMobileTop,
  ChartTypeControl,
  RangeControl,
  useChartControls,
} from "@components/Chart/ChartControls";
import { useAssetClassFilter } from "./assetClassFilter";

// With an asset class filter the chart shows the combined line only.
const COMBINED_ONLY: SecuritiesChartSelectedType[] = ["all"];

const SubtitleContainer = styled("div")({
  display: "flex",
  flexWrap: "wrap",
});

const GainLossPercentsStyled = styled(GainLossPercents)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

const SecuritiesChartStyled = styled(SecuritiesChart)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    boxShadow: "none",
  },
}));

interface Props {
  accountIds: number[];
  currency?: string;
  onStatistic?: (statistic: InvestmentsChartDataStatistic) => void;
}

export const DepotsDashboardChart = ({
  accountIds,
  currency: requestedCurrency,
  onStatistic,
}: Props) => {
  const { t } = useTranslation("");
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const {
    chartType,
    comparedTicker,
    comparisonValues,
    selectedAssetTypes,
    setAvailableAssetTypes,
    rangeProps,
  } = useChartControls();
  const { isFiltered, investmentTypes } = useAssetClassFilter();
  const startDate = startOfDay(addYears(new Date(), -1));

  const chartStartDate = rangeProps.range?.startDate ?? startDate;
  const chartEndDate = rangeProps.range?.endDate;

  const {
    data: chartData,
    statistic,
    typesWithData,
    responseCurrency,
    isFetching,
  } = useInvestmentsChartData(
    accountIds,
    chartStartDate,
    chartEndDate,
    chartType,
    comparedTicker,
    requestedCurrency,
    investmentTypes
  );

  const currency = responseCurrency ?? requestedCurrency ?? "EUR";

  const startValue = chartData[0]?.totalValue ?? 0;
  const endValue = chartData[chartData.length - 1]?.totalValue ?? 0;
  const trendColor = usePerformanceTrendColor(startValue, endValue);

  useEffect(() => {
    onStatistic?.(statistic);
  }, [statistic, onStatistic]);

  useEffect(() => {
    setAvailableAssetTypes(typesWithData);
  }, [typesWithData, setAvailableAssetTypes]);

  return (
    <>
      {/* The chart type switches the figure as well as the chart, so on
          phones its tab line sits above both. */}
      {isSm && (
        <Box mb={4}>
          <ChartControlsMobileTop />
        </Box>
      )}
      <SecuritiesChartStyled
        isFetching={isFetching}
        drawLines={!isSm}
        showNavigator={!isSm}
        heightWithoutNavigator={PHONE_CHART_HEIGHT}
        inlineTooltipOnMobile
        title={
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={2}
            mb={isSm ? 2.5 : 0}
          >
            <MandatorTypography
              variant={isSm ? "h4" : "h2"}
              kpmg-germany={{ variant: isSm ? "h5" : "h3" }}
            >
              {!isFetching &&
                (chartType === "performance"
                  ? toPercent(statistic.performance ?? 0)
                  : isSm
                    ? toCurrencyWithoutLargeCents(
                        statistic.portfolioValue ?? 0,
                        currency
                      )
                    : toCurrency(statistic.portfolioValue ?? 0, currency))}
            </MandatorTypography>
            {!isFetching && (
              <GainLossPercentsStyled
                size="small"
                value={statistic.changeToday ?? 0}
              />
            )}
            {!isFetching && isSm && (
              <Typography variant="caption" color="textSecondary">
                {t("components.depotsStatistic.chart.today")}
              </Typography>
            )}
          </Box>
        }
        chartType={chartType}
        currency={currency}
        subtitle={
          !isSm && (
            <SubtitleContainer>
              <GainLossTypography
                variant="body1"
                value={
                  chartType === "performance"
                    ? (statistic.portfolioValue ?? 0)
                    : (statistic.investedCapital ?? 0)
                }
              >
                {t(`components.depotsStatistic.chart.subheader.${chartType}`)}{" "}
                {!isFetching &&
                  toCurrency(
                    chartType === "performance"
                      ? (statistic.portfolioValue ?? 0)
                      : (statistic.investedCapital ?? 0),
                    currency
                  )}
              </GainLossTypography>
            </SubtitleContainer>
          )
        }
        data={chartData}
        comparedTicker={comparedTicker}
        comparisonValues={comparisonValues}
        selectedTypes={isFiltered ? COMBINED_ONLY : selectedAssetTypes}
        intervalSelector={isSm ? undefined : <RangeControl />}
        chartTypeTabs={isSm ? undefined : <ChartTypeControl />}
        bottomComponent={
          isSm ? (
            <ChartControlsMobileBar />
          ) : (
            <ChartControlsDesktopBar allTypeColor={trendColor} />
          )
        }
      />
    </>
  );
};
